import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { saved as savedApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Tracks which activities the current user has bookmarked. Fetches once when
 * the user logs in and provides toggle/remove helpers so any card can show
 * the heart in the correct state without each component re-fetching.
 *
 * Logged-out callers get an empty set + no-op toggle that the caller can
 * detect and use to prompt for sign-up.
 */
const SavedContext = createContext({
  ids: new Set(),
  items: [],
  loading: false,
  isSaved: () => false,
  toggle: async () => false,
  refresh: async () => {},
});

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); setItems([]); return; }
    setLoading(true);
    try {
      const data = await savedApi.list();
      setIds(new Set(data.ids || []));
      setItems(data.items || []);
    } catch { /* swallow — empty state is fine */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isSaved = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(async (activityId, activityRecord) => {
    if (!user) return false; // caller must handle (e.g. open SignupGateModal)
    const currentlySaved = ids.has(activityId);
    // Optimistic update — flip immediately and revert if the API fails.
    setIds((prev) => {
      const next = new Set(prev);
      if (currentlySaved) next.delete(activityId); else next.add(activityId);
      return next;
    });
    if (activityRecord) {
      setItems((prev) => currentlySaved
        ? prev.filter((a) => a.id !== activityId)
        : [activityRecord, ...prev.filter((a) => a.id !== activityId)]);
    }
    try {
      if (currentlySaved) await savedApi.remove(activityId);
      else await savedApi.add(activityId);
      return !currentlySaved;
    } catch {
      // Rollback
      setIds((prev) => {
        const next = new Set(prev);
        if (currentlySaved) next.add(activityId); else next.delete(activityId);
        return next;
      });
      // Re-sync items from server to be safe
      refresh();
      return currentlySaved;
    }
  }, [ids, user, refresh]);

  const value = useMemo(() => ({ ids, items, loading, isSaved, toggle, refresh }),
    [ids, items, loading, isSaved, toggle, refresh]);

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}

export function useSaved() {
  return useContext(SavedContext);
}
